import { c as _c } from "react/compiler-runtime";
import type { MouseEvent } from "react";
import { Loader2 } from "lucide-react";
import { Button } from "@repo/blacksmith-ui/button";
import { cn } from "@repo/blacksmith-ui/cn";
import { STATUS_COLORS } from "@/components/filters/StatusFilter";
export const KEEP_GOING_ROUNDS = 3;
interface LoopLimitStripProps {
  headline: string;
  /** Trails the compact headline only when the strip has room, e.g. `3 rounds`. */
  headlineMeta?: string;
  detail?: string;
  onKeepGoing?: (event: MouseEvent<HTMLButtonElement>) => void | Promise<void>;
  actionLabel?: string;
  pending: boolean;
  continued: boolean;
  continuedHeadline: string;
  continuedDetail?: string;
  testId: string;
  tabIndex?: number;
  className?: string;
  onClick?: (event: MouseEvent<HTMLDivElement>) => void;
  /** One 32px line with a pill action; the detail line is not rendered. */
  compact?: boolean;
}
export function LoopLimitStrip(t0) {
  const $ = _c(42);
  const {
    headline,
    headlineMeta,
    detail,
    onKeepGoing,
    actionLabel: t1,
    pending,
    continued,
    continuedHeadline,
    continuedDetail,
    testId,
    tabIndex,
    className,
    onClick,
    compact: t2
  } = t0;
  const actionLabel = t1 === undefined ? "Keep going" : t1;
  const compact = t2 === undefined ? false : t2;
  const displayedHeadline = continued ? continuedHeadline : headline;
  const displayedDetail = continued ? continuedDetail : detail;
  const t3 = compact ? "@container h-8 gap-2 pl-2 pr-1" : "flex-wrap gap-x-3 gap-y-1.5 px-3 py-2";
  let t4;
  if ($[0] !== className || $[1] !== t3) {
    t4 = cn("flex items-center justify-between rounded-md border border-[var(--color-border)] bg-[var(--color-surface)]", t3, className);
    $[0] = className;
    $[1] = t3;
    $[2] = t4;
  } else {
    t4 = $[2];
  }
  const t5 = compact ? "items-center" : "items-start";
  let t6;
  if ($[3] !== t5) {
    t6 = cn("flex min-w-0 gap-2", t5);
    $[3] = t5;
    $[4] = t6;
  } else {
    t6 = $[4];
  }
  let t7;
  if ($[5] !== compact || $[6] !== continued || $[7] !== pending) {
    t7 = compact ? <span aria-hidden={true} className="flex size-3 shrink-0 items-center justify-center">{pending && !continued ? <Loader2 className="size-3 animate-spin text-neutral-500" /> : <span className="size-1.5 rounded-full" style={{
        backgroundColor: continued ? "var(--color-border)" : STATUS_COLORS.queued
      }} />}</span> : <span aria-hidden={true} className="mt-[7px] size-1.5 shrink-0 rounded-full" style={{
      backgroundColor: continued ? "var(--color-border)" : STATUS_COLORS.queued
    }} />;
    $[5] = compact;
    $[6] = continued;
    $[7] = pending;
    $[8] = t7;
  } else {
    t7 = $[8];
  }
  const t8 = compact ? "truncate type-body-xs-medium" : "type-body-s-medium";
  let t9;
  if ($[9] !== t8) {
    t9 = cn("text-neutral-900 dark:text-neutral-100", t8);
    $[9] = t8;
    $[10] = t9;
  } else {
    t9 = $[10];
  }
  let t10;
  if ($[11] !== compact || $[12] !== continued || $[13] !== headlineMeta) {
    t10 = compact && headlineMeta && !continued ? <><span className="sr-only"> · {headlineMeta}</span><span aria-hidden={true} className="hidden @min-[22rem]:inline">{" "}· {headlineMeta}</span></> : null;
    $[11] = compact;
    $[12] = continued;
    $[13] = headlineMeta;
    $[14] = t10;
  } else {
    t10 = $[14];
  }
  let t11;
  if ($[15] !== displayedHeadline || $[16] !== t10 || $[17] !== t9) {
    t11 = <p className={t9}>{displayedHeadline}{t10}</p>;
    $[15] = displayedHeadline;
    $[16] = t10;
    $[17] = t9;
    $[18] = t11;
  } else {
    t11 = $[18];
  }
  let t12;
  if ($[19] !== compact || $[20] !== displayedDetail) {
    t12 = displayedDetail && !compact ? <p className="type-body-xs-regular text-neutral-500 dark:text-neutral-400">{displayedDetail}</p> : null;
    $[19] = compact;
    $[20] = displayedDetail;
    $[21] = t12;
  } else {
    t12 = $[21];
  }
  let t13;
  if ($[22] !== t11 || $[23] !== t12) {
    t13 = <div className="min-w-0">{t11}{t12}</div>;
    $[22] = t11;
    $[23] = t12;
    $[24] = t13;
  } else {
    t13 = $[24];
  }
  let t14;
  if ($[25] !== t13 || $[26] !== t6 || $[27] !== t7) {
    t14 = <div className={t6}>{t7}{t13}</div>;
    $[25] = t13;
    $[26] = t6;
    $[27] = t7;
    $[28] = t14;
  } else {
    t14 = $[28];
  }
  let t15;
  if ($[29] !== actionLabel || $[30] !== compact || $[31] !== continued || $[32] !== onKeepGoing || $[33] !== pending || $[34] !== tabIndex) {
    t15 = !continued && onKeepGoing ? <Button type="button" disabled={pending} onClick={onKeepGoing} tabIndex={tabIndex} variant="secondary" size={compact ? "xs" : undefined} className={compact ? "shrink-0" : undefined}>{pending && !compact ? <Loader2 className="mr-1 size-3 animate-spin" /> : null}{actionLabel}</Button> : null;
    $[29] = actionLabel;
    $[30] = compact;
    $[31] = continued;
    $[32] = onKeepGoing;
    $[33] = pending;
    $[34] = tabIndex;
    $[35] = t15;
  } else {
    t15 = $[35];
  }
  let t16;
  if ($[36] !== onClick || $[37] !== t14 || $[38] !== t15 || $[39] !== t4 || $[40] !== testId) {
    t16 = <div className={t4} data-testid={testId} onClick={onClick}>{t14}{t15}</div>;
    $[36] = onClick;
    $[37] = t14;
    $[38] = t15;
    $[39] = t4;
    $[40] = testId;
    $[41] = t16;
  } else {
    t16 = $[41];
  }
  return t16;
}